export const GENERAL_ERROR_LABEL = 'Review.Common.Error';

export const ApiErrorCodes = {
  NICKNAME_ALREADY_EXISTS: 'Review.Common.Nickname.Validation.IDExisted',
  INVALID_NICKNAME: 'Review.Common.Nickname.Validation.Inappropriate',
  USER_ID_ALREADY_EXISTS: 'MyReview.Edit.UserID.Validation.IDExisted',
  INVALID_USER_ID: 'MyReview.Edit.UserID.Validation.Inappropriate',
  USER_WITHDRAWN: 'MyReview.AccountDeleted',
} as const;

export type ApiErrorCodeType = keyof typeof ApiErrorCodes;

export type ApiError<T extends ApiErrorCodeType> = {
  code: T;
  labelId: (typeof ApiErrorCodes)[T] | typeof GENERAL_ERROR_LABEL;
};

export enum Gender {
  MALE = 'MALE',
  FEMALE = 'FEMALE',
}

export type profileMedia = {
  id: string;
  url: string;
};

export enum AgeRange {
  TEN = 'TEN',
  TWENTY = 'TWENTY',
  THIRTY = 'THIRTY',
  FORTY = 'FORTY',
  FIFTY = 'FIFTY',
  SIXTY = 'SIXTY',
  SEVENTY = 'SEVENTY',
  EIGHTY = 'EIGHTY',
}

export type Review = {
  id: string;
  title: string;
  comment: string;
  isAnonymous: boolean;
  isLiked: boolean;
  viewCount: number;
  likeCount: number;
  postDateTime: string;
  reservation: Reservation;
  overallScore: number;
  author: UserProfile;
  replies?: ReviewReply[];
  scores: CategoryRating[];
  questions: CategoryRating[];
  media: Media[];
  provider: Provider;
  hideReason?: string;
  postFrom?: PostType;
  status?: ReviewStatus;
};
export enum ReviewStatus {
  PENDING = 'PENDING',
  POSTED = 'POSTED',
  HIDDEN = 'HIDDEN',
  DELETED = 'DELETED',
}
export enum PostType {
  PC = 'PC',
}

export type UserProfile = {
  userId: string;
  profileAvatar?: profileMedia;
  coverImage?: profileMedia;
  userBio?: string;
  nickname?: string;
  gender: Gender;
  likeCount: number;
  viewCount: number;
  reviewCount: number;
  isAgeAndGenderVisible: boolean;
  ageRange: AgeRange;
  snsLinks?: UserProfileSns[];
  travelPurpose?: string;
  travelCompanion?: string;
};

export type EditProfile = {
  userId: string;
  nickname: string;
  profileAvatar: profileMedia;
  coverImage: profileMedia;
  userBio: string;
  isAgeAndGenderVisible: boolean;
  snsLinks?: UserProfileSns[];
};

export type DefaultSNSValues = {
  platform: SnsPlatform;
  url: string;
};

export type SimpleUserProfile = Required<
  Pick<
    UserProfile,
    | 'userId'
    | 'nickname'
    | 'profileAvatar'
    | 'coverImage'
    | 'userBio'
    | 'isAgeAndGenderVisible'
    | 'snsLinks'
  >
>;

export type Provider = {
  id: string;
  thumbnailUrl?: string;
  name: string;
  isPetsAllowed?: boolean;
  prefecture?: string;
  area?: string;
};

export type ReviewReply = {
  replyId: string;
  reviewId?: string;
  authorType?: AuthorType;
  entityId: string;
  authorName: string;
  postDate: string;
  content: string;
  parentId: string;
  active: boolean;
  deleteDate?: string;
};

export enum AuthorType {
  USER = 'USER',
  PROVIDER = 'PROVIDER',
  INTERNAL = 'INTERNAL_STAFF',
}

export type CategoryRating = {
  category: Category;
  code: string;
  orderNo: number;
  score?: string;
  value?: string;
};

export enum Category {
  SCORE = 'SCORE',
  SCORES = 'SCORES',
  OVERALL_SCORE = 'OVERALL_SCORE',
  COMPANION = 'COMPANION',
  PURPOSE = 'PURPOSE',
  ADDITIONAL_DETAIL = 'ADDITIONAL_DETAIL',
  AI_SUMMARY = 'AI_SUMMARY',
  TOPIC = 'TOPIC',
  STAR_DISTRIBUTION = 'STAR_DISTRIBUTION',
}

export enum CategoryItemCode {
  ONE = 'ONE',
  TWO = 'TWO',
  THREE = 'THREE',
  FOUR = 'FOUR',
  FIVE = 'FIVE',
  ROOM = 'ROOM',
  OVERALL_SCORE = 'OVERALL_SCORE',
  LOCATION = 'LOCATION',
  HOSPITALITY = 'HOSPITALITY',
  BREAKFAST = 'BREAKFAST',
  DINNER = 'DINNER',
  BATH = 'BATH',
  FACILITY = 'FACILITY',
  CLEANLINESS = 'CLEANLINESS',
  LEISURE = 'LEISURE',
  BUSINESS = 'BUSINESS',
  OTHER = 'OTHER',
  SOLO = 'SOLO',
  FAMILY = 'FAMILY',
  LOVER = 'LOVER',
  FRIEND = 'FRIEND',
  COLLEAGUE = 'COLLEAGUE',
  SMALL_CHILD = 'SMALL_CHILD',
  SENIOR_CITIZEN = 'SENIOR_CITIZEN',
  DISABILITIES = 'DISABILITIES',
  PET = 'PET',
  ILLNESS = 'ILLNESS',
}

export type Reservation = {
  reservationId?: string;
  providerId: string;
  checkInDate?: string;
  checkOutDate?: string;
  plan?: Plan;
  item: Item;
  isDinnerIncluded?: boolean;
  isBreakfastIncluded?: boolean;
  adultCount?: number;
  childCount?: number;
  maleGuestCount?: number;
  femaleGuestCount?: number;
  //adding temporarily till api confirms to remove lint errors
  providerName?: string;
  providerLocation?: string;
  providerThumbnailUrl?: string;
};

export type Plan = {
  id: string;
  name: string;
  publicationStartDateTime?: string;
  publicationEndDateTime?: string;
};

export type Item = {
  id: string;
  name: string;
  publicationStartDate?: string;
  publicationEndDate?: string;
};

export type UserProfileSns = {
  platform: SnsPlatform;
  url: string;
  orderNo: number;
};

export enum SnsPlatform {
  INSTAGRAM = 'INSTAGRAM',
  TIKTOK = 'TIKTOK',
  X = 'X',
  YOUTUBE = 'YOUTUBE',
  GOOGLE_MAP = 'GOOGLE_MAP',
  NOTE = 'NOTE',
  ROOM = 'ROOM',
}

export enum MediaType {
  IMAGE = 'IMAGE',
  VIDEO = 'VIDEO',
  DOCUMENT = 'DOCUMENT',
  ICON = 'ICON',
}

export enum MediaStatus {
  OK = 'OK',
  PROCESSING = 'PROCESSING',
  NOT_FOUND = 'NOT_FOUND',
}

export type Media = {
  id: string;
  type: MediaType;
  status: MediaStatus;
  fileName: string;
  url?: string;
  thumbnailUrl?: string;
  videoId?: string;
  orderNo: number;
  width?: number;
  height?: number;
};

export type ReviewMediaItem = Omit<Review, 'media'> & {
  media: Media;
};
